import { match } from "ts-pattern";
import type { RealmId } from "./realm-id";
import type {
  RealmsolverConfig,
  ScopeMatcher,
  NonEmptyArray,
  PinSolverRule,
  RegionSolverRule,
} from "./realmsolver-config";
import { rendezvousSelect } from "./rendezvous-hash";
import type { TenantScope } from "./scope-key";
import { scopeToKey } from "./scope-key";

/**
 * Pure function: scope → target realm. Always returns a value.
 * Totality is guaranteed by construction (non-empty default realms).
 */
export type Realmsolver = (scope: TenantScope) => RealmId;

/**
 * Asserts that an array is non-empty at runtime.
 * Zod's nonempty() validates this, but TypeScript can't infer the tuple type.
 */
const assertNonEmpty = <T>(arr: T[]): NonEmptyArray<T> => {
  if (arr.length === 0) {
    throw new Error("Expected non-empty array");
  }
  return arr as unknown as NonEmptyArray<T>;
};

/**
 * Gets the key value from a scope based on its kind.
 */
const getScopeValue = (scope: TenantScope): string => {
  switch (scope.kind) {
    case "customer":
      return scope.id;
    case "branch":
      return scope.code;
    case "test":
      return scope.unique;
  }
};

/**
 * Tests if a scope matches a ScopeMatcher.
 */
const matchesScope = (scope: TenantScope, matcher: ScopeMatcher): boolean => {
  if (matcher.kind === "all") {
    return true;
  }

  if (matcher.kind !== scope.kind) {
    return false;
  }

  const value = getScopeValue(scope);
  const { pattern } = matcher;

  if (pattern === "*") {
    return true;
  }

  if (pattern.startsWith("^") || pattern.endsWith("$")) {
    // Treat as regex
    try {
      return new RegExp(pattern).test(value);
    } catch {
      return false;
    }
  }

  // Exact match
  return value === pattern;
};

/**
 * Creates a Realmsolver from configuration.
 *
 * The solver is total by construction: you cannot create one without
 * at least one realm in defaultRealms, guaranteeing every scope maps
 * to exactly one realm.
 *
 * Resolution order:
 * 1. Check rules in order - first matching rule wins
 * 2. Fall back to default realms via rendezvous hashing
 *
 * @param config - Realmsolver configuration with rules and defaults
 * @returns A pure function mapping TenantScope to RealmId
 */
export const createRealmsolver = (config: RealmsolverConfig): Realmsolver => {
  // Pre-validate and convert default realms to ensure non-empty at runtime
  const defaultRealms = assertNonEmpty(config.defaultRealms);

  return (scope: TenantScope): RealmId => {
    const key = scopeToKey(scope);

    // Check rules in order
    for (const rule of config.rules) {
      if (!matchesScope(scope, rule.match)) {
        continue;
      }

      return match(rule)
        .with({ kind: "pin" }, (r: PinSolverRule) => r.realm)
        .with({ kind: "region" }, (r: RegionSolverRule) => {
          const realms = assertNonEmpty(r.realms);
          return rendezvousSelect(key, realms);
        })
        .exhaustive();
    }

    // Fall back to default realms
    return rendezvousSelect(key, defaultRealms);
  };
};
